import { cn } from '../../lib/utils'

/**
 * TimeColumns — the scrollable hour / minute / AM-PM column UI, decoupled from
 * any value type: it takes a 24-hour `hour` + `minute` and emits the chosen
 * `(hour24, minute)`. Shared by `TimePicker` (wraps it in a popover with a
 * `Time` value) and `DatePicker` (renders it beside the calendar for date-time
 * granularity, bound to the value's time part).
 */

export type TimeColumnsSize = 'sm' | 'md' | 'lg'
type Period = 'AM' | 'PM'

const pad = (n: number) => String(n).padStart(2, '0')

const cellBase =
  'flex w-full shrink-0 cursor-pointer items-center justify-center rounded-md tabular-nums text-fbui-gray-700 outline-none transition-colors hover:bg-fbui-gray-100 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
// Light "selected value" highlight (matches the month/year dropdown) — reads as
// selected, not as a primary-action button like a solid basil-600 fill would.
const cellSelected =
  'bg-fbui-basil-50 font-medium text-fbui-basil-700 hover:bg-fbui-basil-50'

const cellSize: Record<TimeColumnsSize, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
  lg: 'px-3.5 py-2 text-base',
}

const columnSize: Record<TimeColumnsSize, string> = {
  sm: 'h-44 w-12',
  md: 'h-56 w-16',
  lg: 'h-64 w-20',
}

// Defined at module scope (not inside TimeColumns) so its type identity is
// stable across renders — otherwise React remounts each column on every click,
// resetting the scroll position (picking an hour would snap the minute column
// back to the top).
const Column = ({
  items,
  isSelected,
  onSelect,
  format,
  label,
  size,
}: {
  items: (number | Period)[]
  isSelected: (item: number | Period) => boolean
  onSelect: (item: number | Period) => void
  format: (item: number | Period) => string
  label: string
  size: TimeColumnsSize
}) => (
  <div
    role="listbox"
    aria-label={label}
    className={cn(
      'fbui-scroll flex flex-col gap-0.5 overflow-y-auto px-1',
      columnSize[size]
    )}
  >
    {items.map((item) => (
      <button
        key={String(item)}
        type="button"
        role="option"
        aria-selected={isSelected(item)}
        onClick={() => onSelect(item)}
        className={cn(
          cellBase,
          cellSize[size],
          isSelected(item) && cellSelected
        )}
      >
        {format(item)}
      </button>
    ))}
  </div>
)

export interface TimeColumnsProps {
  /** Current hour (0–23). */
  hour?: number
  /** Current minute (0–59). */
  minute?: number
  hourCycle?: 12 | 24
  minuteStep?: number
  size?: TimeColumnsSize
  /** Called with the chosen 24-hour hour + minute. */
  onChange: (hour24: number, minute: number) => void
  className?: string
}

export const TimeColumns = ({
  hour,
  minute,
  hourCycle = 12,
  minuteStep = 5,
  size = 'md',
  onChange,
  className,
}: TimeColumnsProps) => {
  const hours =
    hourCycle === 24
      ? Array.from({ length: 24 }, (_, i) => i)
      : Array.from({ length: 12 }, (_, i) => i + 1)
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => i * minuteStep
  )

  const h = hour ?? 0
  const m = minute ?? 0
  const period: Period = h >= 12 ? 'PM' : 'AM'
  const displayHour = hourCycle === 24 ? h : h % 12 === 0 ? 12 : h % 12

  const toHour24 = (dh: number, p: Period) =>
    hourCycle === 24 ? dh : p === 'PM' ? (dh % 12) + 12 : dh % 12

  return (
    <div className={cn('flex divide-x divide-fbui-gray-200', className)}>
      <Column
        label="Hour"
        size={size}
        items={hours}
        isSelected={(x) => x === displayHour}
        onSelect={(x) => onChange(toHour24(x as number, period), m)}
        format={(x) => (hourCycle === 24 ? pad(x as number) : String(x))}
      />
      <Column
        label="Minute"
        size={size}
        items={minutes}
        isSelected={(x) => x === m}
        onSelect={(x) => onChange(toHour24(displayHour, period), x as number)}
        format={(x) => pad(x as number)}
      />
      {hourCycle === 12 && (
        <Column
          label="Period"
          size={size}
          items={['AM', 'PM']}
          isSelected={(x) => x === period}
          onSelect={(x) => onChange(toHour24(displayHour, x as Period), m)}
          format={(x) => String(x)}
        />
      )}
    </div>
  )
}
TimeColumns.displayName = 'TimeColumns'
